'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardCheck, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePreferences } from '@/context/PreferencesContext';
import { getETDate, isDuringMarketHours } from '@/lib/marketTime';
import {
  PRE_TRADING_CHECKLIST_ITEMS,
  CHECKLIST_DISMISS_LOCK_SECONDS,
  CHECKLIST_DISMISSED_KEY,
  CHECKLIST_FORCE_SHOW_EVENT,
  CHECKLIST_CHANNEL,
  wasDismissedOn,
  shouldShowChecklist,
} from '@/lib/preTradingChecklist';
import { cn } from '@/lib/utils';

/** Re-check the gate this often so the checklist also pops the moment the
 *  bell rings for a tab that was already open during pre-market. */
const EVAL_INTERVAL_MS = 30_000;

function readDismissedDate(): string | null {
  try { return window.localStorage.getItem(CHECKLIST_DISMISSED_KEY); } catch { return null; }
}

/** Tell sibling tabs the checklist was just dismissed so they hide it too.
 *  Mirrors the buy/sell pending-review popup's BroadcastChannel approach. */
function broadcastDismiss(): void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(CHECKLIST_CHANNEL);
    ch.postMessage({ type: 'dismiss' });
    ch.close();
  } catch { /* ignore — BroadcastChannel can be unavailable in some contexts */ }
}

/**
 * Daily "Pre-Trading Checklist" gate. Mounted site-wide (LayoutShell) and
 * self-gates: it only renders during the regular session on trading days,
 * for logged-in users who haven't disabled it or already dismissed it
 * today. The Dismiss button is locked for 15s with a live countdown; the
 * countdown lives in component state, so a refresh restarts it — and the
 * "dismissed today" flag is written only on an actual dismiss, so you
 * can't refresh-skip the review.
 */
export default function PreTradingChecklistModal() {
  const { user } = useAuth();
  const { showPreTradingChecklist } = usePreferences();
  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(CHECKLIST_DISMISS_LOCK_SECONDS);
  const [checked, setChecked] = useState<boolean[]>(() => PRE_TRADING_CHECKLIST_ITEMS.map(() => false));

  // Read current visibility without making it an effect dependency — keeps
  // the polling interval from resetting an already-open popup's timer.
  const visibleRef = useRef(visible);
  useEffect(() => { visibleRef.current = visible; }, [visible]);

  useEffect(() => {
    // Logged out or feature disabled → ensure hidden. This also closes the
    // popup the instant the user toggles it off, and prevents a flash for an
    // opted-out user whose `false` pref arrives from the DB just after mount
    // (the pref defaults ON, so the short first-eval delay below lets that DB
    // value land before the first possible show).
    if (!user || !showPreTradingChecklist) { setVisible(false); return; }
    const evaluate = () => {
      if (visibleRef.current) return; // already showing; don't restart the lock
      const due = shouldShowChecklist({
        loggedIn: true,
        enabled: true, // guaranteed by the guard above
        duringMarketHours: isDuringMarketHours(),
        dismissedToday: wasDismissedOn(readDismissedDate(), getETDate()),
      });
      if (due) {
        setSecondsLeft(CHECKLIST_DISMISS_LOCK_SECONDS);
        setChecked(PRE_TRADING_CHECKLIST_ITEMS.map(() => false));
        setVisible(true);
      }
    };
    const first = window.setTimeout(evaluate, 1200);
    const id = window.setInterval(evaluate, EVAL_INTERVAL_MS);
    return () => { window.clearTimeout(first); window.clearInterval(id); };
  }, [user, showPreTradingChecklist]);

  // Force-show: the configurations toggle dispatches this when the user
  // enables the checklist, so it pops immediately — bypassing the market-hours
  // and dismissed-today gates (an explicit "show me now" / preview). The
  // event only fires from the authed configurations page, and the eval effect
  // above won't hide it afterward because the pref is now ON.
  useEffect(() => {
    const onForce = () => {
      setSecondsLeft(CHECKLIST_DISMISS_LOCK_SECONDS);
      setChecked(PRE_TRADING_CHECKLIST_ITEMS.map(() => false));
      setVisible(true);
    };
    window.addEventListener(CHECKLIST_FORCE_SHOW_EVENT, onForce);
    return () => window.removeEventListener(CHECKLIST_FORCE_SHOW_EVENT, onForce);
  }, []);

  // 15s dismiss-lock countdown — runs only while visible and not yet unlocked.
  useEffect(() => {
    if (!visible || secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [visible, secondsLeft]);

  // Cross-tab dismiss: when another tab dismisses the checklist it posts on
  // this channel; hide here too so a dismiss in one tab clears it everywhere
  // (the dismissing tab already wrote the shared localStorage flag, so this
  // tab's next gate eval keeps it hidden). Same idiom as the buy/sell
  // pending-review popup.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CHECKLIST_CHANNEL);
    const onMsg = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type === 'dismiss') setVisible(false);
    };
    ch.addEventListener('message', onMsg);
    return () => { ch.removeEventListener('message', onMsg); ch.close(); };
  }, []);

  // Lock background scroll while the checklist is up.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  const handleDismiss = useCallback(() => {
    if (secondsLeft > 0) return; // still locked
    try { window.localStorage.setItem(CHECKLIST_DISMISSED_KEY, getETDate()); } catch { /* ignore */ }
    setVisible(false);
    broadcastDismiss(); // hide it in every other open tab too
  }, [secondsLeft]);

  if (!visible) return null;

  const locked = secondsLeft > 0;
  const reviewed = checked.filter(Boolean).length;
  const progressPct = ((CHECKLIST_DISMISS_LOCK_SECONDS - secondsLeft) / CHECKLIST_DISMISS_LOCK_SECONDS) * 100;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="My Pre-Trading Checklist"
    >
      {/* Backdrop — intentionally not click-to-dismiss; the 15s button is the only exit. */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="relative bg-card-bg border border-default rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Lock progress bar (fills over the 15s) */}
        <div className="h-1 bg-surface flex-shrink-0">
          <div
            className="h-full bg-sky-500/70 transition-[width] duration-1000 ease-linear"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-start gap-3 p-5 border-b border-default flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
            <ClipboardCheck size={18} className="text-sky-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">My Pre-Trading Checklist</h2>
            <p className="text-xs text-fg-subtle mt-0.5">Take a moment to ground yourself before the open.</p>
          </div>
        </div>

        {/* Items */}
        <div className="p-4 overflow-y-auto space-y-1.5">
          {PRE_TRADING_CHECKLIST_ITEMS.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setChecked((c) => c.map((v, j) => (j === i ? !v : v)))}
              className={cn(
                'w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors',
                checked[i] ? 'bg-sky-600/10 border-sky-500/40' : 'border-default hover:bg-surface',
              )}
            >
              <span className={cn(
                'mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors',
                checked[i] ? 'bg-sky-500 border-sky-500' : 'border-slate-500',
              )}>
                {checked[i] && <Check size={13} className="text-white" />}
              </span>
              <span className={cn('text-sm leading-snug', checked[i] ? 'text-sky-200' : 'text-slate-200')}>
                <span className="text-fg-subtle tabular-nums mr-1.5">{i + 1}.</span>{item}
              </span>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-default flex-shrink-0">
          <span className="text-[11px] text-fg-subtle tabular-nums">
            {reviewed} of {PRE_TRADING_CHECKLIST_ITEMS.length} reviewed
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={locked}
            aria-label={locked ? `Dismiss available in ${secondsLeft} seconds` : 'Dismiss checklist'}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-lg transition-colors min-w-[8.5rem] text-center',
              locked
                ? 'bg-surface border border-default text-fg-subtle cursor-not-allowed'
                : 'bg-sky-600 hover:bg-sky-500 text-white',
            )}
          >
            {locked ? `Dismiss in ${secondsLeft}s` : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
}
